import { useEffect } from 'react';

/**
 * Lightweight, dependency-free per-page SEO head manager for CRA/React-Router.
 *
 * Sets / replaces the following tags on mount, and restores nothing on unmount
 * (each page is expected to call this too, so the previous value is simply
 * overwritten).
 *
 *   title
 *   <meta name="description">
 *   <link rel="canonical">
 *   <meta property="og:title">
 *   <meta property="og:description">
 *   <meta property="og:url">
 *   <meta name="twitter:title">
 *   <meta name="twitter:description">
 *
 * It also injects one or more JSON-LD <script type="application/ld+json">
 * blocks tagged with a data-seo-tag attribute so they can be cleaned up on
 * unmount (prevents duplicate JSON-LD when navigating between routes).
 */
const upsertMeta = (selector, attr, value) => {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement('meta');
    const [key, name] = selector.replace('meta[', '').replace(']', '').split('=');
    el.setAttribute(key, name.replace(/"/g, ''));
    document.head.appendChild(el);
  }
  el.setAttribute(attr, value);
};

const upsertLink = (rel, href) => {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
};

const SEO_SCRIPT_ATTR = 'data-seo-tag';

const injectJsonLd = (obj, id) => {
  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.setAttribute(SEO_SCRIPT_ATTR, id);
  script.textContent = JSON.stringify(obj);
  document.head.appendChild(script);
  return script;
};

export const useSEO = ({ title, description, canonical, jsonLd = [] } = {}) => {
  useEffect(() => {
    if (title) document.title = title;
    if (description) {
      upsertMeta('meta[name="description"]', 'content', description);
      upsertMeta('meta[property="og:description"]', 'content', description);
      upsertMeta('meta[name="twitter:description"]', 'content', description);
    }
    if (title) {
      upsertMeta('meta[property="og:title"]', 'content', title);
      upsertMeta('meta[name="twitter:title"]', 'content', title);
    }
    if (canonical) {
      upsertLink('canonical', canonical);
      upsertMeta('meta[property="og:url"]', 'content', canonical);
    }

    const injected = [];
    (jsonLd || []).forEach((obj, i) => {
      injected.push(injectJsonLd(obj, `page-${i}`));
    });

    return () => {
      injected.forEach((s) => s.parentNode && s.parentNode.removeChild(s));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, description, canonical]);
};

export default useSEO;
